/**
 * Reading the measured capacity, and turning it into plain English.
 *
 * Every function here is pure and derives its answer from what
 * `GET /api/v1/capacity` reported. Nothing is guessed: when the server did not
 * measure something, the phrasing says so instead of filling a blank.
 */

import type {
  Capacity,
  CapacityNode,
  GpuSlot,
  GpuState,
  PoolInfo,
} from "../api/types";
import { formatElapsed, pluralize } from "./format";

/** GPU counts the console offers when the server publishes none. */
const OFFERED_COUNTS: readonly number[] = [1, 2, 4, 8];

/** Short label for a machine shape, e.g. `8-GPU machine`. */
export function shapeLabel(gpusPerNode: number): string {
  return `${gpusPerNode}-GPU machine`;
}

/** A GPU family as researchers say it: `h100` → `H100` (ADR-0020). */
export function familyLabel(family: string): string {
  return family.toUpperCase();
}

/**
 * True when the payload holds no entry for this family (ADR-0020).
 *
 * A family appears in `families[]` only when at least one of its pools was
 * really read, so a missing entry means "could not be read", never "does not
 * exist" — and a screen must say that instead of answering from the
 * fleet-wide figures, which aggregate across families.
 *
 * Args:
 *     capacity: The measured capacity payload.
 *     family: The family a screen is reasoning about, if any.
 *
 * Returns:
 *     True when a family was named and the payload cannot describe it.
 */
export function familyIsUnknown(
  capacity: Capacity,
  family: string | null | undefined,
): boolean {
  if (!family) {
    return false;
  }
  return !capacity.families.some((entry) => entry.family === family);
}

/**
 * Which GPU a workload holds, as a label — or nothing at all.
 *
 * A deployment with one kind of GPU names none: there is nothing to tell
 * apart, and repeating "H100" on every count would be noise. With more than
 * one, every count carries its family, so the choice made at creation stays
 * verifiable for the rest of the workload's life.
 *
 * Args:
 *     capacity: The measured capacity payload, if it has been read.
 *     family: The workload's own family; `null` means the deployment default.
 *
 * Returns:
 *     The family label to show beside a GPU count, or null to show none.
 */
export function workloadFamilyLabel(
  capacity: Capacity | undefined,
  family: string | null,
): string | null {
  if (!capacity || capacity.families.length <= 1) {
    return null;
  }
  const held = family ?? capacity.defaults.gpu_family;
  return held ? familyLabel(held) : null;
}

/** `2 H100 GPUs`, or `2 GPUs` where there is only one kind of GPU. */
export function gpuCountLabel(count: number, family: string | null): string {
  if (family === null) {
    return pluralize(count, "GPU");
  }
  return `${count} ${family} ${count === 1 ? "GPU" : "GPUs"}`;
}

/** A machine's family, from its instance type — `H100-SXM-8-80G` → `h100`. */
function nodeFamily(node: CapacityNode): string | null {
  const head = node.instance_type?.split("-", 1)[0]?.trim().toLowerCase();
  return head || null;
}

/**
 * The capacity payload narrowed to one GPU family (ADR-0020).
 *
 * Everything downstream — buttons, sentences, ETAs — then reasons about that
 * family alone, so a free machine of the other GPU can never promise "starts
 * in seconds" to somebody who chose this one. Without a family, the payload
 * is returned as it came: the pre-family meaning, and the one-family case.
 *
 * A family the payload cannot describe comes back empty rather than borrowing
 * the fleet-wide counts, which are an aggregate across families: offering an
 * L40S workspace the sizes an H100 machine holds is the surprise ADR-0020
 * exists to prevent. `familyIsUnknown` is how a screen recognises that state
 * and says so.
 */
function scopedToFamily(
  capacity: Capacity,
  family: string | null | undefined,
): Capacity {
  if (!family) {
    return capacity;
  }
  const entry = capacity.families.find((item) => item.family === family);
  return {
    ...capacity,
    pools: capacity.pools.filter((pool) => pool.family === family),
    nodes: capacity.nodes.filter((node) => nodeFamily(node) === family),
    can_start_now: entry ? entry.can_start_now : [],
    defaults: {
      ...capacity.defaults,
      allowed_gpu_counts: entry ? entry.allowed_gpu_counts : [],
    },
  };
}

/** Numbers pronounced with an initial vowel, so "an 8-GPU machine" reads. */
const VOWEL_NUMBERS = new Set([8, 11, 18, 80]);

/** The shape with its article, e.g. `an 8-GPU machine`, `a 4-GPU machine`. */
export function indefiniteShapeLabel(gpusPerNode: number): string {
  const article = VOWEL_NUMBERS.has(gpusPerNode) ? "an" : "a";
  return `${article} ${shapeLabel(gpusPerNode)}`;
}

/** The pools sorted small shape first, the order every list shows them in. */
export function poolsBySize(capacity: Capacity): PoolInfo[] {
  return [...capacity.pools].sort((a, b) => a.gpus_per_node - b.gpus_per_node);
}

/**
 * The most GPUs one machine can hold, from whatever the server measured.
 *
 * The pools are authoritative when Scaleway could be read. When it could not,
 * the biggest machine actually in the cluster is still a measured fact, so it
 * is used instead of inventing a shape.
 *
 * Args:
 *     capacity: The measured capacity payload.
 *
 * Returns:
 *     The per-machine GPU ceiling, at least 1.
 */
export function gpusPerNode(capacity: Capacity): number {
  const biggestPool = Math.max(
    0,
    ...capacity.pools.map((pool) => pool.gpus_per_node),
  );
  if (biggestPool > 0) {
    return biggestPool;
  }
  const biggestNode = Math.max(
    0,
    ...capacity.nodes.map((node) => node.gpu_total),
  );
  const biggestAllowed = Math.max(0, ...capacity.defaults.allowed_gpu_counts);
  return Math.max(1, biggestNode, biggestAllowed);
}

/** The pools whose machines are big enough to hold `count` GPUs. */
function poolsThatFit(capacity: Capacity, count: number): PoolInfo[] {
  return capacity.pools.filter((pool) => pool.gpus_per_node >= count);
}

/**
 * The pools Scaleway could actually describe.
 *
 * A pool with `error` set is configured but unknown: its counts are zero
 * because nothing was read, not because it is empty — so it must never be
 * counted as "full" or "with room".
 */
function readablePools(pools: PoolInfo[]): PoolInfo[] {
  return pools.filter((pool) => pool.error === null);
}

/** A machine of this pool can still be asked for. `current_nodes` already
 * counts the booting ones, so adding `provisioning_nodes` would declare the
 * pool full one machine early. */
export function poolHasRoom(pool: PoolInfo): boolean {
  return pool.current_nodes < pool.max_nodes;
}

/** One "how many GPUs" button on the request form. */
export interface GpuChoice {
  count: number;
  /** The request would be satisfied immediately. */
  immediate: boolean;
  /** The button cannot be used at all. */
  disabled: boolean;
  /** One sentence explaining what happens if you pick it, always present. */
  reason: string;
}

/**
 * Explain what asking for a given number of GPUs would do right now.
 *
 * A choice is only disabled when it can never succeed: more GPUs than any
 * machine holds, or every machine already running and full. "Needs a new
 * machine" is *not* a refusal — the console starts one and the workspace
 * waits — so the option stays clickable, with the wait spelled out.
 *
 * Args:
 *     count: Number of GPUs the researcher is considering.
 *     capacity: The measured capacity payload.
 *     family: Reason about one GPU family only (ADR-0020); omitted means the
 *         whole fleet — the one-family case, unchanged.
 *
 * Returns:
 *     The choice, its availability and the sentence to show under the button.
 */
export function gpuChoice(
  count: number,
  fleet: Capacity,
  family?: string | null,
): GpuChoice {
  const capacity = scopedToFamily(fleet, family);
  const { can_start_now, node_start_eta_minutes } = capacity;
  const perNode = gpusPerNode(capacity);

  if (count > perNode) {
    return {
      count,
      immediate: false,
      disabled: true,
      reason: `A machine holds ${pluralize(perNode, "GPU")} at most, and a workspace runs on one machine — ask for ${perNode} at most.`,
    };
  }

  if (can_start_now.includes(count)) {
    return {
      count,
      immediate: true,
      disabled: false,
      reason: "Free right now — starts in seconds.",
    };
  }

  if (capacity.pools.length === 0) {
    return {
      count,
      immediate: false,
      disabled: true,
      reason:
        "Not free right now, and the console cannot start another machine — " +
        "it cannot reach Scaleway. A GPU has to be released first.",
    };
  }

  const bigEnough = poolsThatFit(capacity, count);
  const fitting = readablePools(bigEnough);

  if (bigEnough.length > 0 && fitting.length === 0) {
    return {
      count,
      immediate: false,
      disabled: true,
      reason:
        "Not free right now, and the console could not check with Scaleway " +
        "whether a machine can be started. A GPU has to be released first.",
    };
  }

  if (fitting.some((pool) => pool.provisioning_nodes > 0)) {
    return {
      count,
      immediate: false,
      disabled: false,
      reason: `A machine is already starting — about ${node_start_eta_minutes} min.`,
    };
  }

  if (fitting.some(poolHasRoom)) {
    return {
      count,
      immediate: false,
      disabled: false,
      reason: `Needs a new machine — about ${node_start_eta_minutes} min.`,
    };
  }

  return {
    count,
    immediate: false,
    disabled: true,
    reason:
      "Every machine that could hold this is already running and full — " +
      "GPUs have to be given back first.",
  };
}

/**
 * The GPU-count buttons to offer, given the shapes of the pools.
 *
 * Args:
 *     capacity: The measured capacity payload.
 *     family: Offer for one GPU family only (ADR-0020); omitted means the
 *         whole fleet — the one-family case, unchanged.
 *
 * Returns:
 *     One entry per offered count, in ascending order. Empty when a family was
 *     named and the payload cannot say what its machines hold — the caller
 *     shows that in words, it does not guess a list.
 */
export function gpuChoices(
  fleet: Capacity,
  family?: string | null,
): GpuChoice[] {
  const capacity = scopedToFamily(fleet, family);
  const measured = capacity.defaults.allowed_gpu_counts;
  // The built-in list answers "the server published no counts at all", which
  // is a question about the whole fleet. Once a family is named, that
  // family's own list is the only answer — empty included.
  const offered =
    measured.length > 0
      ? [...measured].sort((a, b) => a - b)
      : family
        ? []
        : OFFERED_COUNTS;
  const perNode = gpusPerNode(capacity);
  return offered
    .filter((count) => count <= perNode)
    .map((count) => gpuChoice(count, capacity));
}

/** True when nothing at all can start without booting a machine. */
export function needsNewMachine(capacity: Capacity): boolean {
  return capacity.can_start_now.length === 0;
}

/** True when Scaleway is already building at least one machine for us. */
export function isProvisioning(capacity: Capacity): boolean {
  return (
    capacity.pools.some((pool) => pool.provisioning_nodes > 0) ||
    capacity.nodes.some((node) => node.status === "provisioning")
  );
}

/** True when the pools are known and none of them can grow any further. */
export function poolsAreFull(capacity: Capacity): boolean {
  const readable = readablePools(capacity.pools);
  return readable.length > 0 && !readable.some(poolHasRoom);
}

/** Distinct people holding at least one GPU on a machine. */
export function holdersOf(node: CapacityNode): string[] {
  const names: string[] = [];
  for (const gpu of node.gpus) {
    const user = gpu.holder?.user;
    if (user && !names.includes(user)) {
      names.push(user);
    }
  }
  return names;
}

/** Join names the way a sentence would: `a`, `a and b`, `a, b and c`. */
function joinNames(names: string[]): string {
  if (names.length <= 1) {
    return names[0] ?? "";
  }
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * One sentence describing a machine, at GPU granularity.
 *
 * Example: `8 GPUs — 1 in use by nicolas, 7 free`.
 *
 * Args:
 *     node: A machine from the capacity payload.
 *
 * Returns:
 *     The sentence shown next to the GPU pills.
 */
export function summarizeNode(node: CapacityNode): string {
  if (node.status === "provisioning") {
    return `${pluralize(node.gpu_total, "GPU")} — this machine is still starting`;
  }
  if (node.status === "not_ready") {
    return `${pluralize(node.gpu_total, "GPU")} — this machine is not accepting work`;
  }
  const used = Math.max(0, node.gpu_total - node.gpu_free);
  if (used === 0) {
    return `${pluralize(node.gpu_total, "GPU")} — all free`;
  }
  const names = holdersOf(node);
  const by = names.length > 0 ? ` by ${joinNames(names)}` : "";
  const free =
    node.gpu_free > 0 ? `${node.gpu_free} free` : "none free";
  return `${pluralize(node.gpu_total, "GPU")} — ${used} in use${by}, ${free}`;
}

/**
 * One sentence for a pool of machines, e.g.
 * `2 of 4 running, 1 starting`.
 *
 * `ready_nodes`, not `current_nodes`: the latter is what the pool was asked
 * for and already counts the machines that are still booting, so adding the
 * starting ones would count them twice.
 *
 * A pool with a server-configured floor says so in plain words — a researcher
 * looking at an idle machine must understand it is deliberately kept ready
 * for the next request, not forgotten. A floor that keeps office hours
 * (`floor_hours`, ADR-0024) names them, and says when it is not in force.
 */
export function summarizePool(pool: PoolInfo): string {
  const parts = [`${pool.ready_nodes} of ${pool.max_nodes} running`];
  if (pool.provisioning_nodes > 0) {
    parts.push(`${pool.provisioning_nodes} starting`);
  }
  if (pool.unavailable_nodes > 0) {
    parts.push(`${pool.unavailable_nodes} stuck`);
  }
  // A machine on its way out is said plainly, and never as "stuck": the
  // console asked for it to go, it is going, and nobody has to do anything.
  if (pool.draining_nodes > 0) {
    parts.push(`${pool.draining_nodes} going back to Scaleway`);
  }
  if (pool.floor > 0) {
    const kept = `${pool.floor} kept ready`;
    if (pool.floor_hours === null) {
      parts.push(
        `${kept} even when nobody is computing, so the next request starts ` +
          "in seconds",
      );
    } else if (pool.floor_active) {
      parts.push(
        `${kept} ${pool.floor_hours}, so the next request starts in seconds`,
      );
    } else {
      // Outside the hours the idle machine is not kept: it is going back,
      // and the next request starts a machine first. Said plainly, so an
      // empty pool at night reads as the product working, not as broken.
      parts.push(
        `${kept} ${pool.floor_hours} — outside those hours the pool drains ` +
          "to zero and a request starts a machine first",
      );
    }
  }
  return parts.join(", ");
}

/**
 * Short label for a GPU state, used in legends and labels.
 *
 * `held` is the honest one: somebody has the GPU and the console cannot say
 * whether it is computing — either nothing measures it, or the measurement
 * could not be tied to a container. Calling that "computing" would be a guess.
 */
export const GPU_STATE_LABEL: Record<GpuState, string> = {
  busy: "computing",
  held: "in use",
  idle: "held but idle",
  free: "free",
  unschedulable: "unavailable",
};

/**
 * Describe a single GPU in one sentence, for the accessible name and tooltip.
 *
 * Args:
 *     gpu: The GPU slot as measured.
 *     now: Reference instant in milliseconds (injectable for tests).
 *
 * Returns:
 *     e.g. `GPU 0 — computing, used by nicolas for 1 h 04 (96% busy)`.
 */
export function describeGpu(
  gpu: GpuSlot,
  measured = true,
  now: number = Date.now(),
): string {
  // Without attribution the number is a position in the list, not a device: a
  // researcher reading "GPU 2" would go looking for it in `nvidia-smi`.
  const name = measured ? `GPU ${gpu.index}` : "One GPU";
  const head = `${name} — ${GPU_STATE_LABEL[gpu.state]}`;
  const parts: string[] = [];
  if (gpu.holder) {
    parts.push(
      gpu.holder.since
        ? `used by ${gpu.holder.user} for ${formatElapsed(gpu.holder.since, now)}`
        : `used by ${gpu.holder.user}`,
    );
  } else if (gpu.state === "held") {
    parts.push("used by something outside the console");
  }
  if (gpu.utilization_pct !== null) {
    parts.push(`${Math.round(gpu.utilization_pct)}% busy`);
  }
  return parts.length > 0 ? `${head}, ${parts.join(", ")}` : head;
}

/**
 * Overall sentence for the capacity section header.
 *
 * Args:
 *     capacity: The measured capacity payload.
 *
 * Returns:
 *     e.g. `8 GPUs — 3 in use, 5 free`.
 */
export function summarizeCapacity(capacity: Capacity): string {
  return `${pluralize(capacity.gpu_total, "GPU")} — ${capacity.gpu_used} in use, ${capacity.gpu_free} free`;
}
